import { useDebouncedValue, useElementSize, useLocalStorage } from '@mantine/hooks';
import { GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { DragModal } from 'components/DragModal/DragModal';
import {
  GenericSearchItem,
  getRecordRealId,
} from 'components/Search/components/SearchItemsProvider';
import { SortOption } from 'components/Search/components/SortDropdown';
import { preservedKeys } from 'components/Search/const';
import { getSortOptions } from 'components/Search/functions';
import {
  createContext,
  Fragment,
  memo,
  MutableRefObject,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ResourceContextProvider } from 'react-admin';
import { pick } from 'remeda';
import { PreviewRequest } from 'utility/types/dataProvider';
import { SearchModalBodyEditorial } from './editorial/SearchModalBodyEditorial';
import { SearchModalFooterEditorial } from './editorial/SearchModalFooterEditorial';
import { SearchModalHeaderEditorial } from './editorial/SearchModalHeaderEditorial';
import {
  checkSelectDisabled,
  getAdvFiltersVisiblity,
  getDefaultFilters,
  getDefaultMiniMenuConfig,
  GetDefaultMiniMenuConfigResult,
  getQueryTextKey,
  getSearchModalViewLSKey,
  getSortDirConfig,
  SortDirConfig,
  formatFilterUpdate,
} from './utilities';

/**
 * Search Modal
 *
 * Currently intended to be a lighter weight editorial
 * asset 'search & select' modal for use in various areas
 * accompanying input fields (eg. notifier, etc.)
 *
 * Should not interact with all of full search views
 * code (eg: url qp states, localstorages, etc.) and
 * should be kept generic enough to mount in any area
 * without relying on additional contexts.
 *
 * Note some common components and styles are reused
 * from the main search, but others customized here
 * to make it simpler to trace/maintain and
 * isolate from the full search views logic.
 */

/**
 * Filters payload type
 */
export type SearchModalFilters = {
  /** see: editorial */
  advertorial?: string;
  app?: string[];
  author?: string;
  contributed?: string;
  deleted?: string;
  draft?: string;
  flag?: string[];
  photo_sales?: string;
  print_publication_status?: string;
  print_publication?: string;
  print_slug?: string;
  print_ready?: string;
  'print_depth[max]'?: string;
  'print_depth[min]'?: string;
  'print_publication_date[max]'?: string;
  'print_publication_date[min]'?: string;
  'start_time[max]'?: string;
  'start_time[min]'?: string;
  publish_status?: string[];
  query?: string;
  section?: string[];
  semantic_candidates?: number;
  semantic_nearest?: number;
  semantic?: string;
  subtype?: string | string[];
  template?: string;
  type?: string[];
  uuid?: string[];
  workflow_process?: string | string[];
  workflow?: string | string[];
  /** common pagination and sort */
  limit?: number;
  page?: string | number;
  sort?: string;
  dir?: string;
  /** include previews request */
  preview?: PreviewRequest;
  [key: string]: any;
};

/**
 * Which advanced filters are visible + applied
 * - True if the filter has a value applied
 */
export type FiltersVisibility = {
  showAuthor: boolean;
  showType: boolean;
  showFlag: boolean;
  showContributed: boolean;
  showAdvertorial: boolean;
  showSection: boolean;
  showPrintSlug: boolean;
  showPublishStatus: boolean;
  showStartTime: boolean;
  showDeleted: boolean;
  showPrintReady: boolean;
  showPhotoSales: boolean;
  showApp: boolean;
  showWorkflow: boolean;
  showPrintDepthRange: boolean;
  showPrintPublication: boolean;
  showPrintPublicationDateRange: boolean;
  showPrintPublicationStatus: boolean;
  showTemplate: boolean;
  showDraft: boolean;
};

export type SupportedResources = 'editorial' | string;
export type SelectionModes = 'single' | 'multiple';

/**
 * Updater fn signature, effectively a react state dispatch.
 * Can create eg 'handleSetSelections' in parents for additional
 * logic/filters before setting to the parent selections sate.
 *
 * eg: in parent component...
 * const handleSetSelections = (value) => {
 *   const items = typeof value === 'function' ? value(selectedAssets) : value;
 *   // your custom logic here... then set to parent state...
 *   setSelectedAssets(validItems);
 * }
 */

export type SetSelectionsFn = React.Dispatch<React.SetStateAction<GenericSearchItem[]>>;

export type SearchModalProps = {
  // Specify target resource (eg. 'editorial')
  resource: SupportedResources;
  // Modal open state / close handlers
  isOpen: boolean;
  closeModal: () => void;
  // Selection modes: 'single' | 'multiple'
  selectionMode: SelectionModes;
  // React states for selected items
  selections: GenericSearchItem[];
  setSelections: SetSelectionsFn;
  /**
   * Common accept button handler for footers...
   */
  onAccept?: (
    items: GenericSearchItem[],
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  /**
   * Override default selection disable logic if needed.
   * Checks if item can be selected (eg: exclude current asset, etc...)
   * Return true to disable selection.
   * The item row should also be styled as disabled.
   */
  checkItemSelectDisabled?: (item: GenericSearchItem) => boolean;
  /**
   * Should modal reset internal states on close?
   * eg: reset filters, input, etc when closing.
   * Can be used to allow close/reopen the modal
   * without losing most current states.
   * Does not reset selections as those are
   * handled by the parent state/component.
   */
  resetStatesOnClose?: boolean;
};

export type SearchModalViewTypes = 'list' | 'summary' | 'card';

type HandleSelectItemArgs = {
  item?: GenericSearchItem | null;
};

type SearchModalContextType = {
  // Forwarded props to be accessible via hooks context
  resource: SearchModalProps['resource'];
  isOpen: SearchModalProps['isOpen'];
  closeModal: SearchModalProps['closeModal'];
  selections: SearchModalProps['selections'];
  setSelections: SearchModalProps['setSelections'];
  selectionMode: SearchModalProps['selectionMode'];
  onAccept: SearchModalProps['onAccept'];
  checkItemSelectDisabled?: SearchModalProps['checkItemSelectDisabled'];
  resetStatesOnClose: SearchModalProps['resetStatesOnClose'];
  // Context for internals...
  view: SearchModalViewTypes;
  setView: React.Dispatch<React.SetStateAction<SearchModalViewTypes>>;
  defaultFilters: SearchModalFilters; // default filters
  filters: SearchModalFilters; // full filters payload (visible and hidden)
  dbFilters: SearchModalFilters; // debounced filters for querying
  setFilters: React.Dispatch<React.SetStateAction<SearchModalFilters>>;
  stagedFilters: SearchModalFilters;
  setStagedFilters: React.Dispatch<React.SetStateAction<SearchModalFilters>>;
  showAdvFilters: FiltersVisibility;
  advFilterCount: number;
  // preserved keys when clearing eg: ['query', 'sort', 'dir']
  keptKeys: string[];
  clearFilters: (keepKeys?: string[]) => void;
  // Helper: set/remove filter value per key
  updateFilterValue: (key: keyof SearchModalFilters, value: string | string[]) => void;
  // Helper: set/remove staged filter value per key
  updateStagedFilterValue: (
    key: keyof SearchModalFilters,
    value: string | string[],
  ) => void;
  // Handle item selection toggle
  handleSelectItem: (params: HandleSelectItemArgs) => void;
  clearSelections: () => void;
  // Sort configuration (sortKey, dirKey, upper/lowercase, etc)
  sortDirConfig: SortDirConfig;
  sortOptions: SortOption[];
  // Default query text key (eg: filters['query'])
  queryTextKey: string;
  // Search input ref and related states...
  textInputRef: RefObject<HTMLInputElement>;
  textInputValue: string;
  setTextInputValue: React.Dispatch<React.SetStateAction<string>>;
  dbTextInputValue: string;
  // Ref HTMLDivElement size ref for textfield popover
  textBoxSizeRef: RefObject<any>;
  textBoxWidth: number;
  // Advanced filters popover anchor and setter
  advPopoverAnchorEl: HTMLElement | null;
  advPopoverOpen: boolean;
  setAdvPopoverAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  handleOpenAdvPopover: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseAdvPopover: () => void;
  // Minimenu Setup
  miniMenuConfig: GetDefaultMiniMenuConfigResult;
  // Get record id helper (wrapper around getRecordRealId)
  getRecordId: (record: GenericSearchItem, resource: SupportedResources) => string;
  selectedIds: string[];
  // Miscellaneous... (datagrid ref, helpers, etc)
  resetModalStates: () => void;
  dataGridApiRef: MutableRefObject<GridApiPro | null>;
};

const SearchModalContext = createContext<SearchModalContextType | undefined>(undefined);

export const useSearchModalContext = () => {
  const context = useContext(SearchModalContext);
  if (!context) {
    throw new Error(
      'useSearchModalContext must be used within an SearchModalContextProvider',
    );
  }
  return context;
};

const DEBOUNCE_MS = 500;

/**
 * --------------------------------------------------------
 * Search Modal - Main component
 * Context provider and component entry
 * --------------------------------------------------------
 */
export const SearchModal = memo(
  ({
    resource,
    isOpen,
    closeModal,
    selections,
    setSelections,
    selectionMode,
    onAccept,
    checkItemSelectDisabled,
    resetStatesOnClose = true,
  }: SearchModalProps) => {
    // MUI Datagrid api ref
    const dataGridApiRef = useGridApiRef();

    // Localstorage key for view by resource
    const viewLsKey = useMemo<string>(() => {
      return getSearchModalViewLSKey(resource);
    }, [resource]);

    // Current view set in localstorage
    const [view, setView] = useLocalStorage<SearchModalViewTypes>({
      key: viewLsKey,
      defaultValue: 'summary',
    });

    /**
     * Future note: if adding initial saved search filters
     * as this modal is not concerned about url qp or nav reloads
     * we can just fetch directly from the api and apply?
     */

    // Initial filters on mount
    const defaultFilters = useMemo<SearchModalFilters>(() => {
      return getDefaultFilters(resource);
    }, [resource]);

    // Payload filters (includes hidden eg sort/dir)
    const [filters, setFilters] = useState<SearchModalFilters>(defaultFilters);

    // Debounced filter for api calls delay
    const [dbFilters] = useDebouncedValue(filters, DEBOUNCE_MS);

    // Staged filters for advanced popup
    const [stagedFilters, setStagedFilters] = useState<SearchModalFilters>({});

    // Which advanced filters are visible
    const showAdvFilters: FiltersVisibility = useMemo(() => {
      return getAdvFiltersVisiblity(filters);
    }, [filters]);

    // Count of applied advanced filters
    const advFilterCount = useMemo<number>(() => {
      return Object.values(showAdvFilters).filter(Boolean).length;
    }, [showAdvFilters]);

    // Keep when clearing / 'reset' filters
    const keptKeys = useMemo<string[]>(() => {
      return preservedKeys[resource];
    }, [resource]);

    /**
     * Get records real id helper
     * - Wrapper around getRecordRealId utility
     */
    const getRecordId = useCallback(
      (record: GenericSearchItem, resource: SupportedResources): string => {
        const realId = getRecordRealId(record, resource);
        return realId;
      },
      [],
    );

    /**
     * Array selected IDs for MUI Datagrid selection model
     */
    const selectedIds: string[] = useMemo(() => {
      const allIds = selections.map((item) => getRecordId(item, resource));
      const filteredIds = allIds.filter((id) => Boolean(id));
      const uniqueIds = new Set<string>(filteredIds);
      const idArray = Array.from(uniqueIds);
      return idArray;
    }, [getRecordId, resource, selections]);

    // Clears filters - pass keys arr to preserve
    const clearFilters = useCallback(
      (keepKeys?: string[]) => {
        const keysToPreserve = keepKeys || [];
        const cleared = pick(filters, keysToPreserve);
        setFilters(cleared);
      },
      [filters],
    );

    /**
     * Update filter / staged value helpers
     * - removes if value is empty (eg: '' or [])
     * - otherwise sets the value as provided
     * Used for filters components in combination
     * with SelectDropdownInputs etc, to update filters.
     * This is to avoid directly setting empty values so
     * they wont be included in the requests payload.
     */
    const updateFilterValue = useCallback(
      (key: keyof SearchModalFilters, value: string | string[]) => {
        setFilters((prev) => {
          const updated = formatFilterUpdate({
            filters: prev,
            key: key,
            value: value,
          });
          return updated;
        });
      },
      [],
    );

    const updateStagedFilterValue = useCallback(
      (key: keyof SearchModalFilters, value: string | string[]) => {
        setStagedFilters((prev) => {
          const updated = formatFilterUpdate({
            filters: prev,
            key: key,
            value: value,
          });
          return updated;
        });
      },
      [],
    );

    /**
     * Handle selection of items
     * - Single mode: replaces or removes selection
     * - Multiple mode: toggles item in selections
     */
    const handleSelectItem = useCallback(
      ({ item }: HandleSelectItemArgs) => {
        // guard null/undefined item...
        if (!item) {
          return;
        }

        // Check if the item is not selectable / disabled
        const isSelectDisabled = checkSelectDisabled({
          resource: resource,
          record: item,
          checkItemSelectDisabled: checkItemSelectDisabled,
        });

        if (isSelectDisabled) {
          return;
        }

        // Ensure the item has a valid identifier
        const selectionId = getRecordId(item, resource);
        if (!selectionId) {
          return;
        }

        // Check if the item is already selected
        const isSelected = selections.some((item) => {
          const itemId = getRecordId(item, resource);
          if (!itemId) {
            return false;
          }
          const check = itemId === selectionId;
          return check;
        });

        // Single mode: replace or remove selected
        if (selectionMode === 'single') {
          const newState = isSelected ? [] : [item];
          setSelections(newState);
        }
        // Multiple mode: toggle in/out of selections
        if (selectionMode === 'multiple') {
          const newState = isSelected
            ? selections.filter((item) => item?.uuid !== selectionId)
            : [...selections, item];
          setSelections(newState);
        }
      },
      [
        checkItemSelectDisabled,
        getRecordId,
        resource,
        selectionMode,
        selections,
        setSelections,
      ],
    );

    // Clear all selections
    const clearSelections = useCallback(() => {
      setSelections([]);
    }, [setSelections]);

    /**
     * Search textfield ref / states / key
     */
    const textInputRef = useRef<HTMLInputElement>(null);

    // Key for resource eg 'query' | 'search', etc..
    const queryTextKey: string = useMemo<string>(() => {
      return getQueryTextKey(resource);
    }, [resource]);

    const [textInputValue, setTextInputValue] = useState<string>(
      filters?.[queryTextKey] || '',
    );

    const [dbTextInputValue] = useDebouncedValue(textInputValue, DEBOUNCE_MS);

    /**
     *  Advanced filters popover setup
     */

    // Measure div box for adv filters popover width
    const { ref: textBoxSizeRef, width: textBoxWidth } = useElementSize<HTMLDivElement>({
      box: 'border-box',
    });

    // Advanced filters popover anchor and setter
    const [advPopoverAnchorEl, setAdvPopoverAnchorEl] = useState<HTMLElement | null>(
      null,
    );
    const advPopoverOpen = Boolean(advPopoverAnchorEl);

    // Note: popover sometimes has a wrong width
    // on open in local dev, seems due to animation
    // or heavy render, unsure if this due to hot relaod.
    // If occurs in a real build, use force one rerender
    // call in the open handler to nudge the ref to
    // reattach and correct the width.
    // const forceRender = useForceUpdate();

    // Copy all filters to staged filters on open
    // including any hidden eg sort/dir, etc...
    const handleOpenAdvPopover = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setAdvPopoverAnchorEl(textBoxSizeRef.current);
        setStagedFilters(filters);
      },
      [filters, textBoxSizeRef],
    );

    const handleCloseAdvPopover = useCallback(() => {
      setAdvPopoverAnchorEl(null);
    }, []);

    /**
     * Sort configuration and options
     */
    const sortDirConfig: SortDirConfig = useMemo<SortDirConfig>(() => {
      return getSortDirConfig(resource);
    }, [resource]);

    const sortOptions: SortOption[] = useMemo<SortOption[]>(() => {
      return getSortOptions({ resource });
    }, [resource]);

    /**
     * Minimenu config for resource
     * Note: some states for mini menu (anchorEl, etc)
     * are handled inside the row/card components as to
     * avoid issues with multiple menu mount/css shadow issue.
     */
    const miniMenuConfig = useMemo<GetDefaultMiniMenuConfigResult>(() => {
      return getDefaultMiniMenuConfig({ resource });
    }, [resource]);

    /**
     * Reset filters, staged, and other states to initial
     * eg: can be used before onClose calls, but parent
     * can handle the rest (eg: selections, etc.)
     */
    const resetModalStates = useCallback(() => {
      setFilters(defaultFilters);
      setStagedFilters({});
      setTextInputValue('');
      setAdvPopoverAnchorEl(null);
    }, [defaultFilters]);

    const contextValue = useMemo<SearchModalContextType>(() => {
      const ctx = {
        advFilterCount,
        checkItemSelectDisabled,
        clearFilters,
        clearSelections,
        closeModal,
        dataGridApiRef,
        dbFilters,
        dbTextInputValue,
        defaultFilters,
        filters,
        getRecordId,
        isOpen,
        keptKeys,
        miniMenuConfig,
        onAccept,
        queryTextKey,
        resetModalStates,
        resetStatesOnClose,
        resource,
        selectedIds,
        selectionMode,
        selections,
        handleSelectItem,
        setFilters,
        setSelections,
        setStagedFilters,
        setTextInputValue,
        setView,
        showAdvFilters,
        sortDirConfig,
        sortOptions,
        stagedFilters,
        textBoxSizeRef,
        textBoxWidth,
        textInputRef,
        textInputValue,
        advPopoverAnchorEl,
        advPopoverOpen,
        setAdvPopoverAnchorEl,
        handleOpenAdvPopover,
        handleCloseAdvPopover,
        updateStagedFilterValue,
        updateFilterValue,
        view,
      };
      return ctx;
    }, [
      advFilterCount,
      advPopoverAnchorEl,
      advPopoverOpen,
      checkItemSelectDisabled,
      clearFilters,
      clearSelections,
      closeModal,
      dataGridApiRef,
      dbFilters,
      dbTextInputValue,
      defaultFilters,
      filters,
      getRecordId,
      handleCloseAdvPopover,
      handleOpenAdvPopover,
      isOpen,
      keptKeys,
      miniMenuConfig,
      onAccept,
      queryTextKey,
      resetModalStates,
      resetStatesOnClose,
      resource,
      handleSelectItem,
      selectedIds,
      selectionMode,
      selections,
      setSelections,
      setView,
      showAdvFilters,
      sortDirConfig,
      sortOptions,
      stagedFilters,
      textBoxSizeRef,
      textBoxWidth,
      textInputValue,
      updateFilterValue,
      updateStagedFilterValue,
      view,
    ]);

    return (
      <SearchModalContext.Provider value={contextValue}>
        <SearchModalMain />
      </SearchModalContext.Provider>
    );
  },
);
SearchModal.displayName = 'SearchModal';

/**
 * --------------------------------------------------------
 * Main modal component
 * - Renders DragModal with header, body, footer
 * --------------------------------------------------------
 */

const SearchModalMain = memo(() => {
  const { isOpen, closeModal, resetModalStates, resource, resetStatesOnClose } =
    useSearchModalContext();

  const handleOnClose = useCallback(() => {
    if (resetStatesOnClose) {
      resetModalStates();
    }
    closeModal();
  }, [closeModal, resetModalStates, resetStatesOnClose]);

  // If closed can skip mount (heavy component)
  if (!isOpen) {
    return null;
  }

  return (
    <Fragment>
      <ResourceContextProvider value={resource}>
        <DragModal
          open={isOpen}
          disableEnforceFocus={true}
          preventBackdropClickClose={true}
          preventEscapeKeyClose={true}
          shouldConfirmClose={false}
          isDraggable={true}
          isResizable={true}
          isFullscreenable={true}
          initialWidth={'85vw'}
          initialHeight={'85vh'}
          minHeight={'50vh'}
          minWidth={'50vw'}
          onClose={handleOnClose}
          bodySx={{
            p: 0,
            height: '100%',
            overflow: 'hidden',
          }}
          header={<SearchModalHeader />}
          body={<SearchModalBody />}
          footer={<SearchModalFooter />}
        />
      </ResourceContextProvider>
    </Fragment>
  );
});
SearchModalMain.displayName = 'SearchModalMain';

/**
 * Note: in future we can lazy import/load different
 * headers, bodies, footers based on resource type if
 * this component grows to handle more than a few types.
 * Remember to add <Suspense> to the inner body jsx.
 */

/**
 * --------------------------------------------------------
 * Selector: Search Modal Header
 * - Renders header component per resource
 * --------------------------------------------------------
 */

const SearchModalHeader = memo(() => {
  const { resource } = useSearchModalContext();

  switch (resource) {
    case 'editorial': {
      return <SearchModalHeaderEditorial />;
    }
    default: {
      return null;
    }
  }
});
SearchModalHeader.displayName = 'SearchModalHeader';

/**
 * --------------------------------------------------------
 * Selector: Search Modal Body
 * - Renders body component per resource
 * --------------------------------------------------------
 */
const SearchModalBody = memo(() => {
  const { resource } = useSearchModalContext();

  switch (resource) {
    case 'editorial': {
      return <SearchModalBodyEditorial />;
    }
    default: {
      return null;
    }
  }
});
SearchModalBody.displayName = 'SearchModalBody';

/**
 * --------------------------------------------------------
 * Selector: Search Modal Footer
 * - Renders footer component per resource
 * --------------------------------------------------------
 */
const SearchModalFooter = memo(() => {
  const { resource } = useSearchModalContext();

  switch (resource) {
    case 'editorial': {
      return <SearchModalFooterEditorial />;
    }
    default: {
      return null;
    }
  }
});
SearchModalFooter.displayName = 'SearchModalFooter';
